<template>
  <div class="insight-page">
    <div class="page-title">
      <div>
        <p class="eyebrow">ข้อมูลลูกค้า</p>
        <h2>Customer Insight</h2>
        <p class="subtitle">มุมมองวิเคราะห์ฐานลูกค้า</p>
      </div>
      <div class="status-pill" :class="{ ok: conn.ok, bad: !conn.ok }">
        <span class="material-icons-round">{{ conn.ok ? "check_circle" : "error" }}</span>
        {{ conn.label }}
      </div>
    </div>

    <div v-if="isOverviewLoading" class="state-box">
      <span class="material-icons-round">hourglass_top</span>
      <h3>กำลังโหลดข้อมูล...</h3>
    </div>

    <div v-else-if="overviewError" class="state-box error">
      <span class="material-icons-round">report_problem</span>
      <h3>โหลดข้อมูลไม่สำเร็จ</h3>
      <p>{{ overviewError }}</p>
    </div>

    <div v-else-if="!kpi" class="state-box">
      <span class="material-icons-round">analytics</span>
      <h3>ยังไม่มีข้อมูล Customer Insight</h3>
    </div>

    <template v-else>
      <div class="kpi-grid">
        <div v-for="card in kpiCards" :key="card.label" class="card kpi-card">
          <div class="kpi-icon" :class="card.tone">
            <span class="material-icons-round">{{ card.icon }}</span>
          </div>
          <div class="kpi-label">{{ card.label }}</div>
          <div class="kpi-value">{{ card.value }}</div>
          <div v-if="card.sub" class="kpi-sub" :class="{ down: card.down }">{{ card.sub }}</div>
        </div>
      </div>

      <div class="alert-board">
        <div class="alert-board-head">
          <div>
            <h3>สรุปภาพรวมผู้บริหาร</h3>
            <p>รวมภาพฐานลูกค้าและสัญญาณที่ควรดูวันนี้</p>
          </div>
          <span class="alert-board-badge" :class="executiveAlertTone">{{ executiveAlertLabel }}</span>
        </div>
        <div class="alert-grid">
          <div v-for="alert in executiveAlerts" :key="alert.label" class="alert-card" :class="alert.tone">
            <div class="alert-icon">
              <span class="material-icons-round">{{ alert.icon }}</span>
            </div>
            <div>
              <strong>{{ alert.label }}</strong>
              <p>{{ alert.text }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="section-title">องค์ประกอบฐานลูกค้า</div>
      <div class="two-col">
        <div class="card">
          <div class="card-head">
            <div>
              <h3>Risk x Value Matrix</h3>
              <p>จำนวนลูกค้าแต่ละกลุ่ม แกนนอนคือคุณค่า แกนตั้งคือความเสี่ยง</p>
            </div>
          </div>
          <div class="matrix">
            <template v-for="rowDef in matrixRows" :key="rowDef.label">
              <div class="axis-y">{{ rowDef.label }}</div>
              <div v-for="key in rowDef.cells" :key="key" class="matrix-cell" :class="cellClass[key]">
                <div class="cell-name">{{ tierLabel[key] }}</div>
                <div class="cell-count">{{ n(tier(key).count) }}</div>
                <div class="cell-pct">{{ tier(key).pct }}%</div>
                <div class="cell-tip">
                  <div class="cell-tip-name">{{ tierLabel[key] }}</div>
                  <div class="cell-tip-row"><span>ลูกค้า</span><b>{{ n(tier(key).count) }} ราย ({{ tier(key).pct }}%)</b></div>
                  <div class="cell-tip-row"><span>รายได้ปี {{ revenueYearBE }}</span><b>฿{{ m(tier(key).revenue) }}M</b></div>
                  <div class="cell-tip-row"><span>คิดเป็นรายได้</span><b>{{ tier(key).revenuePct }}% ของทั้งหมด</b></div>
                </div>
              </div>
            </template>
          </div>
          <div class="axis-x">
            <span></span>
            <span>คุณค่าต่ำ</span>
            <span>คุณค่ากลาง</span>
            <span>คุณค่าสูง</span>
          </div>
        </div>

        <div class="card">
          <div class="card-head">
            <div>
              <h3>สัดส่วนตามระดับความเสี่ยง</h3>
              <p>แบ่งฐานลูกค้าออกเป็น 3 แถบใหญ่</p>
            </div>
          </div>
          <div class="risk-wrap">
            <div class="chart-box donut">
              <canvas ref="bandDonut"></canvas>
            </div>
            <div class="bars">
              <div v-for="b in bands" :key="b.band" class="bar-row">
                <div class="bar-name">
                  {{ bandLabel[b.band] }}
                  <small>{{ bandSub[b.band] }}</small>
                </div>
                <div class="track">
                  <div class="fill" :style="{ width: b.pct + '%', background: bandColor[b.band] }"></div>
                </div>
                <div class="bar-value">{{ b.pct }}%</div>
              </div>
              <div class="inline-note">
                ลูกค้าเสี่ยงต่ำ {{ lowBand.pct }}% สร้างดอกเบี้ย
                <strong>{{ lowBand.revenuePct }}%</strong> ของรายได้ปี {{ revenueYearBE }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card chart-card">
        <div class="card-head">
          <div>
            <h3>ที่มาของรายได้ตามระดับความเสี่ยง</h3>
            <p>ดอกเบี้ยที่แต่ละกลุ่มความเสี่ยงสร้างให้ร้าน {{ revCmp }}</p>
          </div>
          <div class="card-head-right">
            <div class="hint-wrap">
              <button class="hint-btn" :class="{ on: showRevenueHint }" @click="showRevenueHint = !showRevenueHint">
                <span class="material-icons-round">info</span>
              </button>
              <div v-if="showRevenueHint" class="hint-pop hint-pop-left">
                <div class="hp-title">วิธีอ่านกราฟ</div>
                <div class="hp-row"><span class="hp-sq" style="background:#1faa5e"></span><span><b>แท่งเขียว</b> — ดอกเบี้ยจากลูกค้า<b>เสี่ยงต่ำ</b> (Platinum/Gold/Silver)</span></div>
                <div class="hp-row"><span class="hp-sq" style="background:#f59e0b"></span><span><b>แท่งเหลือง</b> — ลูกค้า<b>เสี่ยงปานกลาง</b> (Watch/Preferred/Standard)</span></div>
                <div class="hp-row"><span class="hp-sq" style="background:#ef4444"></span><span><b>แท่งแดง</b> — ลูกค้า<b>เสี่ยงสูง</b> (Caution/Guard/Manage)</span></div>
                <div class="hp-row"><span class="hp-dot" style="background:#111827"></span><span><b>เส้นดำ</b> — ดอกเบี้ยรวมต่องวด (เฉพาะโหมดบาท)</span></div>
                <div class="hp-sep"></div>
                <div class="hp-title">2 โหมด</div>
                <div class="hp-body"><b>สัดส่วน %</b> — แต่ละงวดรวม 100% เห็น mix รายได้ว่าพึ่งกลุ่มไหน</div>
                <div class="hp-body"><b>บาท</b> — มูลค่าดอกเบี้ยจริง (ล้านบาท) ต่องวด</div>
                <div class="hp-sep"></div>
                <div class="hp-title">วิธีตีความ</div>
                <div class="hp-body">ถ้าสัดส่วนรายได้จากกลุ่ม<b>เสี่ยงสูง</b>โตขึ้นเรื่อย ๆ = ร้านพึ่งพารายได้จากลูกค้าเสี่ยงมากขึ้น ควรเฝ้าระวังคุณภาพพอร์ต</div>
                <div class="hp-sep"></div>
                <div class="hp-body" style="color:#9a6a00"><b>ข้อควรระวัง:</b> ระดับความเสี่ยงคิดจากสถานะ<b>ปัจจุบัน</b>ของลูกค้า — ดอกเบี้ยย้อนหลังถูกจัดเข้ากลุ่มที่ลูกค้าเป็น<b>วันนี้</b> (ไม่ใช่ ณ ตอนนั้น) เพราะระบบไม่ได้เก็บประวัติ tier งวดเก่า ๆ จึงสะท้อน "segment ปัจจุบันเคยทำเงินมาเท่าไหร่"</div>
              </div>
            </div>
            <div class="period-toggle">
              <button
                v-for="p in periods"
                :key="p.k"
                :class="{ active: revenuePeriod === p.k }"
                type="button"
                @click="changeRevenue(p.k)"
              >
                {{ p.t }}
              </button>
            </div>
          </div>
        </div>
        <div v-if="!revenueBandState" class="rev-toolbar">
          <div class="period-toggle">
            <button :class="{ active: revenueMode === 'pct' }" type="button" @click="setRevenueMode('pct')">สัดส่วน %</button>
            <button :class="{ active: revenueMode === 'amount' }" type="button" @click="setRevenueMode('amount')">บาท</button>
          </div>
        </div>
        <div v-if="!revenueBandState" class="revenue-risk-card" :class="revenueRisk.tone">
          <div class="revenue-risk-main">
            <div class="revenue-risk-icon">
              <span class="material-icons-round">{{ revenueRisk.icon }}</span>
            </div>
            <div>
              <span>Revenue at Risk</span>
              <strong>{{ revenueRisk.title }}</strong>
              <p>{{ revenueRisk.text }}</p>
            </div>
          </div>
          <div class="revenue-risk-stats">
            <div>
              <span>ดอกเบี้ยล่าสุด</span>
              <strong>฿{{ revenueRisk.total }}M</strong>
            </div>
            <div>
              <span>จากกลุ่มเสี่ยงสูง</span>
              <strong>{{ revenueRisk.highPct }}%</strong>
            </div>
            <div>
              <span>เปลี่ยนจากงวดก่อน</span>
              <strong :class="{ down: revenueRisk.delta < 0 }">{{ signedPct(revenueRisk.delta) }}</strong>
            </div>
          </div>
        </div>
        <div v-if="revenueBandState" class="section-state chart-placeholder" :class="revenueBandState.type">
          <span class="material-icons-round">{{ revenueBandState.icon }}</span>
          <strong>{{ revenueBandState.title }}</strong>
          <p>{{ revenueBandState.text }}</p>
        </div>
        <div v-else class="chart-box tall">
          <canvas ref="revenueChart"></canvas>
        </div>
      </div>

      <div class="section-title">การแบ่งกลุ่มเชิงพฤติกรรม</div>
      <div class="card">
        <div class="card-head">
          <div>
            <h3>RFM Segmentation</h3>
            <p>Recency, Frequency และ Monetary แสดงเป็น 7 กลุ่มลูกค้า</p>
          </div>
        </div>
        <div v-if="rfmState" class="section-state" :class="rfmState.type">
          <span class="material-icons-round">{{ rfmState.icon }}</span>
          <strong>{{ rfmState.title }}</strong>
          <p>{{ rfmState.text }}</p>
        </div>
        <div v-else class="segment-grid">
          <div v-for="s in rfmRows" :key="s.key" class="segment-card" :style="{ '--tone': segMeta[s.key].color }">
            <span class="material-icons-round">{{ segMeta[s.key].icon }}</span>
            <div class="seg-name">{{ segMeta[s.key].name }}</div>
            <div class="seg-count">{{ n(s.count) }}</div>
            <div class="seg-desc">{{ segMeta[s.key].desc }}</div>
          </div>
        </div>
      </div>

      <div class="card chart-card">
        <div class="card-head">
          <div>
            <h3>การเติบโตของฐานลูกค้า</h3>
            <p>ลูกค้าใหม่ ลูกค้าเก่า และอัตรากลับมาทำรายการ {{ cmpLabel }} — นับจากลูกค้าที่ทำรายการงวดก่อนแล้วกลับมาทำอีกในงวดนี้</p>
          </div>
          <div class="toggle-with-month">
            <div class="period-toggle">
              <button
                v-for="p in periodsWithDay"
                :key="p.k"
                :class="{ active: growthPeriod === p.k }"
                type="button"
                @click="changePeriod(p.k)"
              >
                {{ p.t }}
              </button>
            </div>
            <input
              v-if="growthPeriod === 'day'"
              type="month"
              class="period-month-input"
              :value="growthMonth"
              @change="changeGrowthMonth($event.target.value)"
            />
          </div>
        </div>
        <div v-if="growthBaseState" class="section-state chart-placeholder" :class="growthBaseState.type">
          <span class="material-icons-round">{{ growthBaseState.icon }}</span>
          <strong>{{ growthBaseState.title }}</strong>
          <p>{{ growthBaseState.text }}</p>
        </div>
        <div v-else class="chart-box tall">
          <canvas ref="growthChart"></canvas>
        </div>
      </div>

      <div class="two-col">
        <div class="card">
          <div class="card-head">
            <div>
              <h3>พฤติกรรมการทำรายการ</h3>
              <p>{{ behaviorScope === 'year' ? `สัดส่วนธุรกรรมปี ${behaviorYearBE}` : 'สัดส่วนธุรกรรมตลอดอายุลูกค้า' }}</p>
            </div>
            <div class="period-toggle">
              <button :class="{ active: behaviorScope === 'lifetime' }" type="button" @click="changeBehaviorScope('lifetime')">ทั้งหมด</button>
              <button :class="{ active: behaviorScope === 'year' }" type="button" @click="changeBehaviorScope('year')">ปีนี้</button>
            </div>
          </div>
          <div v-if="behaviorState" class="section-state" :class="behaviorState.type">
            <span class="material-icons-round">{{ behaviorState.icon }}</span>
            <strong>{{ behaviorState.title }}</strong>
            <p>{{ behaviorState.text }}</p>
          </div>
          <div v-else class="bars behavior">
            <div v-for="s in behaviorRows" :key="s.type" class="bar-row">
              <div class="bar-name">
                {{ behaviorLabel[s.type].name }}
                <small>{{ behaviorLabel[s.type].sub }}</small>
              </div>
              <div class="track">
                <div class="fill" :style="{ width: s.pct + '%', background: behaviorLabel[s.type].color }"></div>
              </div>
              <div class="bar-value">{{ s.pct }}%</div>
            </div>
            <p class="behavior-note">% คิดจากจำนวนรายการ (ตั๋ว) ไม่ใช่มูลค่าเงินต้น</p>
          </div>
        </div>

        <div class="card chart-card">
          <div class="card-head">
            <div>
              <h3>การกระจุกตัวของรายได้</h3>
              <p>{{ paretoScope === 'year' ? `ดอกเบี้ยปี ${paretoYearBE} เรียงจากลูกค้ามูลค่าสูงสุด` : 'ดอกเบี้ยสะสมเรียงจากลูกค้ามูลค่าสูงสุด' }}</p>
            </div>
            <div class="card-head-right">
              <div class="hint-wrap">
              <button class="hint-btn" :class="{ on: showParetoHint }" @click="showParetoHint = !showParetoHint">
                <span class="material-icons-round">info</span>
              </button>
              <div v-if="showParetoHint" class="hint-pop hint-pop-left">
                <div class="hp-title">วิธีอ่านกราฟ</div>
                <div class="hp-row">
                  <span class="hp-sq" style="background:#c79320"></span>
                  <span><b>แท่งสีทอง</b> — ดอกเบี้ยของกลุ่มลูกค้านั้น (ล้านบาท)</span>
                </div>
                <div class="hp-row">
                  <span class="hp-dot" style="background:#0d6efd"></span>
                  <span><b>เส้นสีน้ำเงิน</b> — สัดส่วนดอกเบี้ยสะสม (%)</span>
                </div>
                <div class="hp-row">
                  <span class="hp-sq" style="background:#e9ecef"></span>
                  <span><b>แกน X</b> — กลุ่มลูกค้าเรียงจากผู้สร้างดอกเบี้ยสูงสุด (Top 5% → 100%)</span>
                </div>
                <div class="hp-sep"></div>
                <div class="hp-title">วิธีตีความ</div>
                <div class="hp-body">ยิ่งเส้นพุ่งสูงเร็วตั้งแต่ต้น แสดงว่าพอร์ตมีการ<b>กระจุกตัวสูง</b> — ลูกค้าน้อยรายแต่แบกรายได้ส่วนใหญ่</div>
                <div class="hp-sep"></div>
                <div class="hp-title">ระดับการกระจุกตัว (ที่ Top 18%)</div>
                <div class="hp-level"><span class="hp-dot" style="background:#ef4444"></span><span class="hp-pct">≥ 80%</span><span>กระจุกตัวสูงมาก · concentration risk สูง</span></div>
                <div class="hp-level"><span class="hp-dot" style="background:#f59e0b"></span><span class="hp-pct">60–79%</span><span>กระจุกตัวสูง · ควรมี early-warning</span></div>
                <div class="hp-level"><span class="hp-dot" style="background:#1f9bbf"></span><span class="hp-pct">40–59%</span><span>กระจุกตัวปานกลาง</span></div>
                <div class="hp-level"><span class="hp-dot" style="background:#1faa5e"></span><span class="hp-pct">&lt; 40%</span><span>กระจุกตัวต่ำ · พอร์ตกระจายตัวดี</span></div>
              </div>
            </div>
            <div class="period-toggle">
              <button :class="{ active: paretoScope === 'lifetime' }" type="button" @click="changeParetoScope('lifetime')">ทั้งหมด</button>
              <button :class="{ active: paretoScope === 'year' }" type="button" @click="changeParetoScope('year')">ปีนี้</button>
            </div>
            </div>
          </div>
          <div v-if="paretoState" class="section-state chart-placeholder" :class="paretoState.type">
            <span class="material-icons-round">{{ paretoState.icon }}</span>
            <strong>{{ paretoState.title }}</strong>
            <p>{{ paretoState.text }}</p>
          </div>
          <div v-else class="chart-box">
            <canvas ref="paretoChart"></canvas>
          </div>
          <div v-if="!paretoState && paretoNote" class="inline-note">
            ลูกค้า {{ paretoNote.pct }}% สร้างดอกเบี้ย {{ paretoNote.cum }}% ของทั้งหมด
          </div>
        </div>
      </div>

      <div class="section-title">การเติบโตของพอร์ต</div>
      <div class="portfolio-grid">
        <div class="card portfolio-unified-card">

          <!-- ── Section 1: Quality of Growth ── -->
          <div class="card-head">
            <div>
              <h3>Quality of Growth</h3>
              <p>เงินต้นเปิดใหม่ เงินต้นที่ปิด และการเติบโตสุทธิ {{ qCmp }}</p>
            </div>
            <div class="card-head-right">
              <div class="hint-wrap">
                <button class="hint-btn" :class="{ on: showQofGHint }" @click="showQofGHint = !showQofGHint">
                  <span class="material-icons-round">info</span>
                </button>
                <div v-if="showQofGHint" class="hint-pop hint-pop-left">
                  <div class="hp-title">วิธีอ่านกราฟ</div>
                  <div class="hp-row"><span class="hp-sq" style="background:#1faa5e"></span><span><b>แท่งเขียว</b> — เงินต้นเปิดใหม่ (ล้านบาท)</span></div>
                  <div class="hp-row"><span class="hp-sq" style="background:#ef4444"></span><span><b>แท่งแดง</b> — เงินต้นที่ปิด (ล้านบาท)</span></div>
                  <div class="hp-row"><span class="hp-dot" style="background:#111827"></span><span><b>เส้นดำ</b> — เติบโตสุทธิ = ไหลเข้า − ไหลออก</span></div>
                  <div class="hp-sep"></div>
                  <div class="hp-title">เงินต้นเปิดใหม่ — รายการที่ทำให้เงินต้นคงค้างเพิ่มขึ้น</div>
                  <div class="hp-level"><span class="hp-dot" style="background:#1faa5e"></span><span class="hp-pct">จำนำใหม่</span><span>ปล่อยเงินต้นก้อนใหม่เข้าพอร์ต</span></div>
                  <div class="hp-level"><span class="hp-dot" style="background:#1faa5e"></span><span class="hp-pct">เพิ่มต้น</span><span>เงินต้นเพิ่มเท่าส่วนต่าง (new − old)</span></div>
                  <div class="hp-sep"></div>
                  <div class="hp-title">เงินต้นที่ปิด — รายการที่ทำให้เงินต้นคงค้างลดลง</div>
                  <div class="hp-level"><span class="hp-dot" style="background:#ef4444"></span><span class="hp-pct">ไถ่ถอน</span><span>ลูกค้าจ่ายเงินต้นคืนครบ ตั๋วปิด</span></div>
                  <div class="hp-level"><span class="hp-dot" style="background:#ef4444"></span><span class="hp-pct">ลดต้น</span><span>จ่ายเงินต้นบางส่วนคืน เฉพาะส่วนต่าง (old − new)</span></div>
                  <div class="hp-level"><span class="hp-dot" style="background:#ef4444"></span><span class="hp-pct">หลุด</span><span>ทรัพย์หลุด เงินต้นตัดออกจากพอร์ต</span></div>
                  <div class="hp-sep"></div>
                  <div class="hp-body"><b>ต่อดอก</b> ไม่อยู่ในกราฟ — เงินต้นไม่เปลี่ยน จ่ายแค่ดอกเบี้ย</div>
                  <div class="hp-sep"></div>
                  <div class="hp-title">วิธีตีความ</div>
                  <div class="hp-body">เส้นสุทธิ <b>บวก (+)</b> = เงินต้นคงค้างขยาย · <b>ลบ (−)</b> = พอร์ตหดตัว</div>
                </div>
              </div>
              <div class="period-toggle">
                <button
                  v-for="p in periodsWithDay"
                  :key="p.k"
                  :class="{ active: qualityPeriod === p.k }"
                  type="button"
                  @click="changeQuality(p.k)"
                >
                  {{ p.t }}
                </button>
              </div>
              <input
                v-if="qualityPeriod === 'day'"
                type="month"
                class="period-month-input"
                :value="qualityMonth"
                @change="changeQualityMonth($event.target.value)"
              />
            </div>
          </div>

          <template v-if="!growthQualityState">
            <div class="mini-grid">
              <div class="mini-stat">
                <span>เงินต้นคงค้างปัจจุบัน</span>
                <strong>฿{{ m(kpi.openPrincipal) }}M</strong>
              </div>
              <div class="mini-stat" :class="latestNet >= 0 ? 'good' : 'danger'">
                <span>เติบโตสุทธิงวดล่าสุด</span>
                <strong>{{ signM(latestNet) }}</strong>
              </div>
              <div class="mini-stat primary">
                <span>โตจากลูกค้าใหม่จริง</span>
                <strong>{{ latestNewPct }}%</strong>
              </div>
            </div>

            <div class="growth-readout" :class="growthQualityStatus.tone">
              <span class="material-icons-round">{{ growthQualityStatus.icon }}</span>
              <div>
                <strong>{{ growthQualityStatus.label }}</strong>
                <p>{{ growthQualityStatus.text }}</p>
                <p class="readout-hint"><span class="material-icons-round">touch_app</span>กดที่ slice ใน donut ด้านล่างเพื่อดูรายละเอียดประเภททรัพย์</p>
              </div>
              <div class="growth-chips">
                <span>ใหม่จริง {{ latestNewPct }}%</span>
                <span>ฐานเดิม {{ latestOldPct }}%</span>
              </div>
            </div>

            <div class="chart-box flow">
              <canvas ref="flowChart"></canvas>
            </div>
          </template>
          <div v-else class="section-state chart-placeholder flow-placeholder" :class="growthQualityState.type">
            <span class="material-icons-round">{{ growthQualityState.icon }}</span>
            <strong>{{ growthQualityState.title }}</strong>
            <p>{{ growthQualityState.text }}</p>
          </div>

          <!-- ── Divider ── -->
          <div class="portfolio-divider">
            <span>รายละเอียดของการขยายตัวและการหดตัวของพอร์ต</span>
          </div>

          <!-- ── Sections 2 & 3 side by side ── -->
          <div class="portfolio-flow-detail">

            <!-- Section 2: ที่มาพอร์ตขยายตัว -->
            <div class="portfolio-section">
              <div class="portfolio-sub-head">
                <div>
                  <h4>ที่มาพอร์ตขยายตัว</h4>
                  <p>เทียบสัดส่วนเงินไหลเข้า 3 งวดล่าสุด</p>
                </div>
                <div class="hint-wrap">
                  <button class="hint-btn" :class="{ on: showInflowHint }" @click="showInflowHint = !showInflowHint">
                    <span class="material-icons-round">info</span>
                  </button>
                  <div v-if="showInflowHint" class="hint-pop hint-pop-left">
                    <div class="hp-title">วิธีอ่านกราฟ</div>
                    <div class="hp-row"><span class="hp-dot" style="background:#3b82f6"></span><span><b>น้ำเงิน</b> — ลูกค้าใหม่จริง (จำนำครั้งแรกในระบบ)</span></div>
                    <div class="hp-row"><span class="hp-dot" style="background:#10b981"></span><span><b>เขียว</b> — ลูกค้าเก่า — จำนำใหม่ (ตั๋วเดิมปิดแล้ว กลับมาเปิดใหม่)</span></div>
                    <div class="hp-row"><span class="hp-dot" style="background:#8b5cf6"></span><span><b>ม่วง</b> — เพิ่มต้น — ตั๋วเดิม (ขอเพิ่มวงเงินบนตั๋วที่ยังเปิดอยู่)</span></div>
                    <div class="hp-row"><span class="hp-sq" style="background:#e9ecef"></span><span><b>% กลาง donut</b> — สัดส่วนลูกค้าใหม่จริง ของงวดนั้น</span></div>
                    <div class="hp-sep"></div>
                    <div class="hp-title">วิธีตีความ</div>
                    <div class="hp-body">ดูแนวโน้มเปรียบเทียบ 3 งวดล่าสุด — ถ้าสัดส่วน<b>น้ำเงินเพิ่มขึ้น</b> แสดงว่าพอร์ตโตจากฐานลูกค้าใหม่ ถ้าสัดส่วน<b>ม่วง/เขียวสูง</b> = เติบโตจากลูกค้าเดิม ควรเร่งหาลูกค้าใหม่เพิ่ม</div>
                  </div>
                </div>
              </div>
              <div v-if="growthQualityState" class="section-state chart-placeholder" :class="growthQualityState.type">
                <span class="material-icons-round">{{ growthQualityState.icon }}</span>
                <strong>{{ growthQualityState.title }}</strong>
                <p>{{ growthQualityState.text }}</p>
              </div>
              <div v-else class="composition-content">
                <div class="composition-grid">
                  <div v-for="(d, i) in donutData" :key="d.label" class="composition">
                    <div class="comp-label" :class="{ current: i === donutData.length - 1 }">{{ d.label }}</div>
                    <div class="chart-box comp-donut">
                      <canvas :ref="(el) => setQualityDonut(el, i)"></canvas>
                      <div class="donut-center">
                        <strong>{{ d.newPct }}%</strong>
                        <span>ใหม่จริง</span>
                      </div>
                    </div>
                    <div class="comp-total">฿{{ d.total }}M</div>
                  </div>
                </div>
                <div class="legend">
                  <span><i class="new"></i> ลูกค้าใหม่จริง</span>
                  <span><i class="returning"></i> ลูกค้าเก่า — จำนำใหม่</span>
                  <span><i class="increase"></i> เพิ่มต้น — ตั๋วเดิม</span>
                </div>
                <div class="insight-note">
                  <span class="material-icons-round">lightbulb</span>
                  <p>{{ qualityInsight }}</p>
                </div>
              </div>
            </div>

            <!-- Vertical separator -->
            <div class="portfolio-vsep"></div>

            <!-- Section 3: สาเหตุพอร์ตหดตัว -->
            <div class="portfolio-section">
              <div class="portfolio-sub-head">
                <div>
                  <h4>สาเหตุพอร์ตหดตัว</h4>
                  <p>เทียบสัดส่วนเงินไหลออก 3 งวดล่าสุด</p>
                </div>
                <div class="hint-wrap">
                  <button class="hint-btn" :class="{ on: showOutflowHint }" @click="showOutflowHint = !showOutflowHint">
                    <span class="material-icons-round">info</span>
                  </button>
                  <div v-if="showOutflowHint" class="hint-pop hint-pop-left">
                    <div class="hp-title">วิธีอ่านกราฟ</div>
                    <div class="hp-row"><span class="hp-dot" style="background:#f43f5e"></span><span><b>แดง</b> — ไถ่ถอน (ปิดตั๋วปกติ จ่ายคืนครบ)</span></div>
                    <div class="hp-row"><span class="hp-dot" style="background:#f59e0b"></span><span><b>เหลือง</b> — ลดต้น (จ่ายบางส่วน เฉพาะส่วนต่างที่ลด)</span></div>
                    <div class="hp-row"><span class="hp-dot" style="background:#d946ef"></span><span><b>ม่วง</b> — หลุด (ทรัพย์หลุด เงินต้นตัดออก)</span></div>
                    <div class="hp-row"><span class="hp-sq" style="background:#e9ecef"></span><span><b>% กลาง donut</b> — สัดส่วนไถ่ถอน ของงวดนั้น</span></div>
                    <div class="hp-sep"></div>
                    <div class="hp-title">วิธีตีความ</div>
                    <div class="hp-body">ยิ่งสัดส่วน<b>แดงสูง</b> แสดงว่าลูกค้าปิดตั๋วได้ปกติ พอร์ตสุขภาพดี — ถ้า<b>ม่วงโต</b>ขึ้นเรื่อย ๆ ควรเฝ้าระวังคุณภาพสินทรัพย์</div>
                  </div>
                </div>
              </div>
              <div v-if="growthQualityState" class="section-state chart-placeholder" :class="growthQualityState.type">
                <span class="material-icons-round">{{ growthQualityState.icon }}</span>
                <strong>{{ growthQualityState.title }}</strong>
                <p>{{ growthQualityState.text }}</p>
              </div>
              <div v-else class="composition-content">
                <div class="composition-grid">
                  <div v-for="(d, i) in outflowDonutData" :key="d.label" class="composition">
                    <div class="comp-label" :class="{ current: i === outflowDonutData.length - 1 }">{{ d.label }}</div>
                    <div class="chart-box comp-donut">
                      <canvas :ref="(el) => setOutflowDonut(el, i)"></canvas>
                      <div class="donut-center">
                        <strong>{{ d.redeemPct }}%</strong>
                        <span>ไถ่ถอน</span>
                      </div>
                    </div>
                    <div class="comp-total">฿{{ d.total }}M</div>
                  </div>
                </div>
                <div class="legend">
                  <span><i class="redeem"></i> ไถ่ถอน</span>
                  <span><i class="reduce-out"></i> ลดต้น</span>
                  <span><i class="forfeit"></i> หลุด</span>
                </div>
                <div class="insight-note">
                  <span class="material-icons-round">lightbulb</span>
                  <p>{{ outflowInsight }}</p>
                </div>
              </div>
            </div>

          </div>

          <!-- ── Asset drill-down panel ── -->
          <transition name="drill-slide">
            <div v-if="drilldown" class="asset-drilldown">
              <div class="asset-drilldown-head">
                <span class="material-icons-round" :style="{ color: drilldown.color }">donut_small</span>
                <strong>ประเภททรัพย์ — {{ drilldown.name }}</strong>
                <span class="drill-period-badge">{{ drilldown.periodLabel }}</span>
                <button class="drill-close" @click="drilldown = null">
                  <span class="material-icons-round">close</span>
                </button>
              </div>
              <div v-if="drilldown.loading" class="drill-empty">
                <span class="material-icons-round">hourglass_empty</span> กำลังโหลด...
              </div>
              <div v-else-if="!drilldown.items.length" class="drill-empty">ไม่มีข้อมูลในงวดนี้</div>
              <div v-else class="drill-bars">
                <div v-for="item in drilldown.items" :key="item.assetType" class="drill-row">
                  <div class="drill-label">{{ item.assetType }}</div>
                  <div class="drill-bar-wrap">
                    <div class="drill-bar" :style="{ width: item.pct + '%', background: drilldown.color }"></div>
                  </div>
                  <div class="drill-pct">{{ item.pct }}%</div>
                  <div class="drill-count">{{ item.count.toLocaleString() }} รายการ</div>
                  <div class="drill-value">{{ fmtDrillValue(item.value) }}</div>
                </div>
              </div>
            </div>
          </transition>

        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Chart, registerables } from "chart.js";
import { useInsightStore } from "@/stores/insight.js";
import { useCusLineStore } from "@/stores/cusline";

Chart.register(...registerables);
const APP_FONT = '"THSarabunNew", "Sukhumvit Set", "Prompt", "Tahoma", "Roboto", sans-serif';
Chart.defaults.font.family = APP_FONT;
Chart.defaults.font.size = 14;

const store = useInsightStore();
const storecusline = useCusLineStore();
const numOfLineCustomer = ref(0);

async function loadLineCustomerCount() {
  const key = JSON.parse(localStorage.getItem("shop_seleted"));
  if (!key) return;
  numOfLineCustomer.value = 0;
  const res = await storecusline.cusLineListFetch({
    index: "0", size: "1", cusUid: "",
    shopUid: key.uid, groupUid: key.groupUid || "",
  });
  const ext = res?.data?.data?.ext;
  if (ext && ext.total !== undefined) numOfLineCustomer.value = ext.total;
}

const kpi = computed(() => store.overview?.kpi);
const bands = computed(() => store.overview?.riskBands ?? []);

const C = {
  good: "#1faa5e",
  warn: "#f59e0b",
  bad: "#ef4444",
  primary: "#344767",
  accent: "#0d6efd",
  net: "#111827",
  purple: "#7c5cff",
  info: "#1f9bbf",
  gold: "#c79320",
  line: "#e9ecef",
};

const kpiCards = computed(() => [
  {
    label: "ลูกค้าทั้งหมด",
    value: n(kpi.value?.totalCustomers),
    sub: kpi.value?.newCustomersLatest
      ? `เพิ่มขึ้น ${n(kpi.value.newCustomersLatest)} คน จากเดือนที่แล้ว`
      : null,
    icon: "groups",
    tone: "primary",
  },
  {
    label: "จำนวนลูกค้า LINE OA",
    value: n(numOfLineCustomer.value),
    sub: `${kpi.value?.totalCustomers ? ((numOfLineCustomer.value / kpi.value.totalCustomers) * 100).toFixed(2) : '0.00'}% ของลูกค้าทั้งหมด`,
    icon: "chat",
    tone: "success",
  },
  {
    label: "ลูกค้า Active",
    value: n(kpi.value?.activeCustomers),
    sub: `${pctOf(kpi.value?.activeCustomers, kpi.value?.totalCustomers)}% ของฐาน - เงินต้น ฿${m(kpi.value?.openPrincipal)}M`,
    icon: "verified",
    tone: "success",
  },
  {
    label: "ลูกค้าใหม่เดือนล่าสุด",
    value: n(kpi.value?.newCustomersLatest),
    sub: `${arrow(kpi.value?.newCustomersDeltaPct)} ${absPct(kpi.value?.newCustomersDeltaPct)}% MoM`,
    down: (kpi.value?.newCustomersDeltaPct ?? 0) < 0,
    icon: "person_add",
    tone: "info",
  },
  {
    label: "ลูกค้าไม่ active > 9 เดือน",
    value: n(kpi.value?.dormantCustomers),
    sub: `${arrow(kpi.value?.dormantDeltaPct)} ${absPct(kpi.value?.dormantDeltaPct)}% เสี่ยงหลุดฐาน`,
    down: (kpi.value?.dormantDeltaPct ?? 0) > 0,
    icon: "bedtime",
    tone: "danger",
  },
]);

const matrixRows = [
  { label: "เสี่ยงต่ำ", cells: ["SILVER", "GOLD", "PLATINUM"] },
  { label: "ปานกลาง", cells: ["STANDARD", "PREFERRED", "WATCH"] },
  { label: "เสี่ยงสูง", cells: ["CAUTION", "GUARD", "MANAGE"] },
];
const cellClass = {
  PLATINUM: "c-plat",
  GOLD: "c-gold",
  SILVER: "c-silv",
  WATCH: "c-watch",
  PREFERRED: "c-pref",
  STANDARD: "c-std",
  MANAGE: "c-mng",
  GUARD: "c-grd",
  CAUTION: "c-cau",
};
const tierLabel = {
  PLATINUM: "Platinum",
  GOLD: "Gold",
  SILVER: "Silver",
  WATCH: "ติดตาม",
  PREFERRED: "ปานกลาง",
  STANDARD: "ทั่วไป",
  MANAGE: "ติดตามใกล้ชิด",
  GUARD: "เฝ้าระวัง",
  CAUTION: "ระวัง",
};
const tierMap = computed(() => Object.fromEntries((store.overview?.tierMatrix ?? []).map((t) => [t.tier, t])));
const tier = (key) => tierMap.value[key] ?? { count: 0, pct: 0, revenue: 0, revenuePct: 0 };
const revenueYearBE = computed(() => {
  const y = store.overview?.tierRevenueYear;
  return y ? y + 543 : "ล่าสุด";
});

const bandLabel = { LOW: "เสี่ยงต่ำ", MID: "ปานกลาง", HIGH: "เสี่ยงสูง" };
const bandSub = { LOW: "Platinum/Gold/Silver", MID: "Watch/Preferred/Standard", HIGH: "Caution/Guard/Manage" };
const bandColor = { LOW: C.good, MID: C.warn, HIGH: C.bad };
const lowBand = computed(() => bands.value.find((b) => b.band === "LOW") ?? { pct: 0, revenuePct: 0 });

const rfmRows = computed(() => store.rfm?.segments ?? []);
const segMeta = {
  champions: { icon: "workspace_premium", name: "Champions", color: C.good, desc: "มาบ่อย มูลค่าสูง ดูแลแบบ VIP" },
  loyal: { icon: "diamond", name: "Loyal", color: "#37bfa7", desc: "ลูกค้าประจำ ไถ่ถอนตรงเวลา" },
  potential: { icon: "trending_up", name: "Potential", color: C.accent, desc: "เริ่มกลับมาใช้ซ้ำ มูลค่ากำลังโต" },
  new: { icon: "fiber_new", name: "New", color: C.purple, desc: "ทำรายการครั้งแรกในช่วงล่าสุด" },
  atRisk: { icon: "warning_amber", name: "At Risk", color: C.warn, desc: "เคยมูลค่าสูง แต่เริ่มหายไป" },
  hibernating: { icon: "nightlight", name: "Hibernating", color: "#e0843a", desc: "เงียบนาน มูลค่าต่ำ" },
  lost: { icon: "remove_circle_outline", name: "Lost", color: C.bad, desc: "ไม่ทำรายการนานและมีความเสี่ยงหลุด" },
};

const periods = [
  { k: "month", t: "เดือน" },
  { k: "quarter", t: "ไตรมาส" },
  { k: "year", t: "ปี" },
];
// Customer-base growth and Quality of Growth (portfolio growth) both get a 4th "day" tab;
// revenue-by-band doesn't support it on the backend yet — kept as its own array so "วัน"
// only shows up on the two toggles that actually support it.
const periodsWithDay = [...periods, { k: "day", t: "วัน" }];
const growthPeriod = ref("month");
const qualityPeriod = ref("month");
const growthMonth = ref(""); // "yyyy-MM", only meaningful while growthPeriod === "day"
const qualityMonth = ref(""); // "yyyy-MM", only meaningful while qualityPeriod === "day"
const cmpMap = { month: "MoM", quarter: "QoQ", year: "YoY", day: "DoD" };
const cmpLabel = computed(() => cmpMap[growthPeriod.value]);
const qCmp = computed(() => store.growthQuality?.comparison ?? cmpMap[qualityPeriod.value]);

async function changePeriod(period) {
  growthPeriod.value = period;
  await store.setGrowthPeriod(period, period === "day" ? growthMonth.value : undefined);
  // Sync the month picker to whatever the backend resolved (defaults to the latest data month
  // the first time "วัน" is opened, since growthMonth starts empty).
  if (period === "day") growthMonth.value = store.growthBase?.month ?? growthMonth.value;
}

async function changeGrowthMonth(month) {
  growthMonth.value = month;
  await store.setGrowthPeriod("day", month);
}

async function changeQuality(period) {
  qualityPeriod.value = period;
  await store.setQualityPeriod(period, period === "day" ? qualityMonth.value : undefined);
  // Sync the month picker to whatever the backend resolved (defaults to the latest data month
  // the first time "วัน" is opened, since qualityMonth starts empty).
  if (period === "day") qualityMonth.value = store.growthQuality?.month ?? qualityMonth.value;
}

async function changeQualityMonth(month) {
  qualityMonth.value = month;
  await store.setQualityPeriod("day", month);
}

const revenuePeriod = ref("month");
const revenueMode = ref("pct"); // 'pct' | 'amount'
const revCmp = computed(() => store.revenueBand?.comparison ?? cmpMap[revenuePeriod.value]);

async function changeRevenue(period) {
  revenuePeriod.value = period;
  await store.setRevenuePeriod(period);
}

function setRevenueMode(mode) {
  revenueMode.value = mode;
  renderRevenue();
}

const behaviorScope = ref("year");
const paretoScope = ref("year");
const currentCEYear = new Date().getFullYear();
const behaviorYearBE = currentCEYear + 543;
const paretoYearBE = currentCEYear + 543;

async function changeBehaviorScope(scope) {
  behaviorScope.value = scope;
  await store.setBehaviorScope(scope === "year" ? currentCEYear : null);
}

async function changeParetoScope(scope) {
  paretoScope.value = scope;
  await store.setParetoScope(scope === "year" ? currentCEYear : null);
}

const behaviorRows = computed(() => store.behavior?.mix ?? []);
const behaviorLabel = {
  REDEEM: { name: "ไถ่ถอน", sub: "ปิดสวย จ่ายครบ", color: "#f43f5e" },
  RENEW: { name: "ต่อดอก", sub: "ยืดสัญญา จ่ายดอก", color: "#10b981" },
  REDUCE: { name: "ลดต้น", sub: "ทยอยลดภาระ", color: "#f59e0b" },
  INCREASE: { name: "เพิ่มต้น", sub: "ตีราคาใหม่ รับเพิ่ม", color: "#8b5cf6" },
  FORFEIT: { name: "หลุด", sub: "ไม่ไถ่ถอน", color: "#d946ef" },
  POSTPONE: { name: "ตั๋วที่ผลัด", sub: "ขอเลื่อนชำระ", color: "#d97706" },
};

const gq = computed(() => store.growthQuality);
const inflowTotal = computed(() => {
  const g = gq.value;
  if (!g) return [];
  return (g.labels ?? []).map((_, i) => (g.inflowNew[i] ?? 0) + (g.inflowReturning[i] ?? 0) + (g.inflowIncrease[i] ?? 0));
});
const lastIdx = computed(() => (gq.value?.labels?.length ?? 0) - 1);
const latestNet = computed(() => (lastIdx.value >= 0 ? gq.value.net[lastIdx.value] : 0));
const latestNewPct = computed(() => {
  const i = lastIdx.value;
  if (i < 0) return 0;
  const total = inflowTotal.value[i];
  return total > 0 ? Math.round(((gq.value.inflowNew[i] ?? 0) / total) * 100) : 0;
});
const latestOldPct = computed(() => Math.max(0, 100 - latestNewPct.value));
const outflowDonutEls = ref([]);
const outflowDonutInsts = [];
function setOutflowDonut(el, index) {
  if (el) outflowDonutEls.value[index] = el;
}

const outflowDonutData = computed(() => {
  const g = gq.value;
  if (!g) return [];
  const out = [];
  for (let k = 2; k >= 0; k -= 1) {
    const i = lastIdx.value - k;
    if (i < 0) continue;
    const data = [g.outflowRedeem[i] ?? 0, g.outflowReduce[i] ?? 0, g.outflowForfeit[i] ?? 0];
    const total = data.reduce((sum, value) => sum + value, 0);
    out.push({
      label: g.labels[i],
      startDate: g.periodStarts?.[i] ?? null,
      endDate: g.periodEnds?.[i] ?? null,
      data,
      total: total.toFixed(1),
      redeemPct: total > 0 ? Math.round((data[0] / total) * 100) : 0,
    });
  }
  return out;
});

const outflowInsight = computed(() => {
  const d = outflowDonutData.value.at(-1);
  if (!d || Number(d.total) === 0) return "";
  if (d.redeemPct >= 70) return `งวดล่าสุดเงินไหลออก ฿${d.total}M — ${d.redeemPct}% เป็นไถ่ถอนปกติ สัญญาณพอร์ตสุขภาพดี`;
  if (d.redeemPct >= 50) return `งวดล่าสุดเงินไหลออก ฿${d.total}M — ${d.redeemPct}% เป็นไถ่ถอน ควรติดตามสัดส่วนหลุดและลดต้น`;
  return `งวดล่าสุดเงินไหลออก ฿${d.total}M — ไถ่ถอนมีเพียง ${d.redeemPct}% ควรตรวจสอบสาเหตุที่ทำให้หลุด/ลดต้นสูง`;
});

const showOutflowHint = ref(false);

const donutData = computed(() => {
  const g = gq.value;
  if (!g) return [];
  const out = [];
  for (let k = 2; k >= 0; k -= 1) {
    const i = lastIdx.value - k;
    if (i < 0) continue;
    const data = [g.inflowNew[i] ?? 0, g.inflowReturning[i] ?? 0, g.inflowIncrease[i] ?? 0];
    const total = data.reduce((sum, value) => sum + value, 0);
    out.push({
      label: g.labels[i],
      startDate: g.periodStarts?.[i] ?? null,
      endDate: g.periodEnds?.[i] ?? null,
      data,
      total: total.toFixed(1),
      newPct: total > 0 ? Math.round((data[0] / total) * 100) : 0,
    });
  }
  return out;
});
const qualityInsight = computed(() => {
  if (lastIdx.value < 0) return "";
  const oldPct = 100 - latestNewPct.value;
  return `งวดล่าสุดพอร์ตโต ${signM(latestNet.value)} (${qCmp.value}) - เงินต้นเปิดใหม่ ${oldPct}% มาจากลูกค้าเก่า (จำนำใหม่ + เพิ่มต้น) และ ${latestNewPct.value}% จากลูกค้าใหม่จริง`;
});

// ── Asset drill-down ────────────────────────────────────────────────────
const INFLOW_TX            = ["PAWN",  "PAWN",       "INCREASE"];
const INFLOW_CUSTOMER_TYPE = ["NEW",   "RETURNING",  null];
const INFLOW_NAME          = ["จำนำ (ลูกค้าใหม่จริง)", "จำนำ (ลูกค้าเก่ากลับมา)", "เพิ่มต้น"];
const INFLOW_COLOR = ["#3b82f6", "#10b981", "#8b5cf6"];
const OUTFLOW_TX   = ["REDEEM", "REDUCE", "FORFEIT"];
const OUTFLOW_NAME = ["ไถ่ถอน", "ลดต้น", "หลุด"];
const OUTFLOW_COLOR = ["#f43f5e", "#f59e0b", "#d946ef"];

const drilldown = ref(null); // { side, donutIdx, sliceIdx, txType, name, color, periodLabel, items, loading }

async function handleDrilldown(side, donutIdx, sliceIdx, startDate, endDate, periodLabel) {
  if (!startDate || !endDate) return;
  const key = `${side}-${donutIdx}-${sliceIdx}`;
  if (drilldown.value?.key === key) { drilldown.value = null; return; }

  const txType = side === "inflow" ? INFLOW_TX[sliceIdx]    : OUTFLOW_TX[sliceIdx];
  const name   = side === "inflow" ? INFLOW_NAME[sliceIdx]  : OUTFLOW_NAME[sliceIdx];
  const color  = side === "inflow" ? INFLOW_COLOR[sliceIdx] : OUTFLOW_COLOR[sliceIdx];

  const customerType = side === "inflow" ? INFLOW_CUSTOMER_TYPE[sliceIdx] : null;
  drilldown.value = { key, side, donutIdx, sliceIdx, txType, name, color, periodLabel, items: [], loading: true };
  try {
    const res = await store.fetchAssetBreakdown(txType, startDate, endDate, customerType);
    drilldown.value = { ...drilldown.value, items: res?.items ?? [], loading: false };
  } catch {
    drilldown.value = { ...drilldown.value, items: [], loading: false };
  }
}

const paretoTone = computed(() => {
  const cum = paretoNote.value?.cum ?? 0;
  if (cum >= 80) return "danger";
  if (cum >= 60) return "warning";
  if (cum >= 40) return "info";
  return "success";
});

const growthQualityStatus = computed(() => {
  const net = latestNet.value;
  const newPct = latestNewPct.value;
  if (net < 0) {
    return {
      tone: "danger",
      icon: "trending_down",
      label: "พอร์ตหดตัว",
      text: `เงินต้นสุทธิงวดล่าสุด ${signM(net)} แม้มีเงินต้นเปิดใหม่ ควรดูสาเหตุเงินต้นที่ปิดและกลุ่มลูกค้าที่หลุด`,
    };
  }
  if (newPct >= 25) {
    return {
      tone: "success",
      icon: "verified",
      label: "โตคุณภาพดี",
      text: `พอร์ตโตสุทธิ ${signM(net)} และมีลูกค้าใหม่จริง ${newPct}% ช่วยลดการพึ่งพาฐานลูกค้าเดิม`,
    };
  }
  if (newPct >= 12) {
    return {
      tone: "info",
      icon: "insights",
      label: "โตแบบสมดุล",
      text: `พอร์ตโตสุทธิ ${signM(net)} โดยยังมีลูกค้าใหม่จริง ${newPct}% ควบคู่กับลูกค้าเก่าที่กลับมาใช้ซ้ำ`,
    };
  }
  return {
    tone: "warning",
    icon: "campaign",
    label: "โตจากฐานเดิม",
    text: `พอร์ตโตสุทธิ ${signM(net)} แต่ลูกค้าใหม่จริงมีเพียง ${newPct}% ควรเติม acquisition เพื่อให้การโตยั่งยืนขึ้น`,
  };
});

const revenueRisk = computed(() => {
  const rb = store.revenueBand;
  const last = (rb?.labels?.length ?? 0) - 1;
  if (!rb || last < 0) {
    return {
      tone: "info",
      icon: "monitoring",
      title: "ยังไม่มีข้อมูลรายได้ตามความเสี่ยง",
      text: "เมื่อข้อมูลดอกเบี้ยตาม risk band พร้อม ระบบจะแสดงรายได้ที่พึ่งพากลุ่มเสี่ยงสูงที่นี่",
      total: "0.00",
      highPct: "0.0",
      delta: 0,
    };
  }

  const currentTotal = rb.total[last] ?? 0;
  const currentHigh = rb.high[last] ?? 0;
  const previousTotal = last > 0 ? rb.total[last - 1] ?? 0 : 0;
  const previousHigh = last > 0 ? rb.high[last - 1] ?? 0 : 0;
  const highPct = currentTotal > 0 ? (currentHigh / currentTotal) * 100 : 0;
  const previousHighPct = previousTotal > 0 ? (previousHigh / previousTotal) * 100 : highPct;
  const delta = Math.round((highPct - previousHighPct) * 10) / 10;
  const highPctRounded = Math.round(highPct * 10) / 10;

  if (highPctRounded >= 35 || (highPctRounded >= 25 && delta > 2)) {
    return {
      tone: "danger",
      icon: "priority_high",
      title: "พึ่งรายได้จากกลุ่มเสี่ยงสูงมาก",
      text: `งวดล่าสุดดอกเบี้ยจากลูกค้าเสี่ยงสูงอยู่ที่ ${highPctRounded.toFixed(1)}% ของรายได้รวม ควรเฝ้าดูการหลุดและลดต้นของกลุ่มนี้ใกล้ชิด`,
      total: currentTotal.toFixed(2),
      highPct: highPctRounded.toFixed(1),
      delta,
    };
  }

  if (highPctRounded >= 20 || delta > 3) {
    return {
      tone: "warning",
      icon: "warning_amber",
      title: "รายได้จากกลุ่มเสี่ยงสูงเริ่มน่าติดตาม",
      text: `สัดส่วน high-risk revenue อยู่ที่ ${highPctRounded.toFixed(1)}% (${signedPct(delta)} จากงวดก่อน) ควรดู mix รายได้และสาเหตุพอร์ตหดตัวประกอบ`,
      total: currentTotal.toFixed(2),
      highPct: highPctRounded.toFixed(1),
      delta,
    };
  }

  return {
    tone: "success",
    icon: "verified",
    title: "รายได้จากกลุ่มเสี่ยงสูงยังอยู่ในระดับควบคุมได้",
    text: `งวดล่าสุด high-risk revenue อยู่ที่ ${highPctRounded.toFixed(1)}% ของดอกเบี้ยรวม และเปลี่ยน ${signedPct(delta)} จากงวดก่อน`,
    total: currentTotal.toFixed(2),
    highPct: highPctRounded.toFixed(1),
    delta,
  };
});

const executiveAlerts = computed(() => {
  const activePct = pctOf(kpi.value?.activeCustomers, kpi.value?.totalCustomers);
  const dormantDelta = kpi.value?.dormantDeltaPct ?? 0;
  const concentration = paretoNote.value;
  return [
    {
      icon: "verified",
      tone: "success",
      label: "ฐานลูกค้าใช้งานอยู่",
      text: `Active ${n(kpi.value?.activeCustomers)} ราย (${activePct}% ของฐาน) พร้อมเงินต้นคงค้าง ฿${m(kpi.value?.openPrincipal)}M`,
    },
    {
      icon: growthQualityStatus.value.icon,
      tone: growthQualityStatus.value.tone,
      label: "คุณภาพการโต",
      text: growthQualityStatus.value.text,
    },
    {
      icon: revenueRisk.value.icon,
      tone: revenueRisk.value.tone,
      label: "Revenue at Risk",
      text: revenueRisk.value.text,
    },
    {
      icon: "account_balance_wallet",
      tone: paretoTone.value,
      label: "การกระจุกตัวของรายได้",
      text: concentration
        ? `Top ${concentration.pct}% ของลูกค้าสร้างดอกเบี้ย ${concentration.cum}% หากสูงขึ้นควรมีแผนดูแลลูกค้าหลัก`
        : "ยังไม่มีข้อมูล Pareto สำหรับประเมินการพึ่งพาลูกค้าหลัก",
    },
    {
      icon: dormantDelta > 0 ? "notifications_active" : "bedtime",
      tone: dormantDelta > 0 ? "warning" : "info",
      label: "ฐานลูกค้าเริ่มเงียบ",
      text: `${n(kpi.value?.dormantCustomers)} รายไม่ active เกิน 9 เดือน (${arrow(dormantDelta)} ${absPct(dormantDelta)}%)`,
    },
  ];
});

const toneScore = { success: 0, info: 1, warning: 2, danger: 3 };
const executiveAlertTone = computed(() => {
  const worst = executiveAlerts.value.reduce((max, item) => Math.max(max, toneScore[item.tone] ?? 0), 0);
  return Object.keys(toneScore).find((tone) => toneScore[tone] === worst) ?? "info";
});
const executiveAlertLabel = computed(() => {
  if (executiveAlertTone.value === "danger") return "ต้องติดตามใกล้ชิด";
  if (executiveAlertTone.value === "warning") return "มีสัญญาณเฝ้าระวัง";
  if (executiveAlertTone.value === "success") return "ภาพรวมปกติ";
  return "ข้อมูลพร้อมอ่าน";
});

const showParetoHint = ref(false);
const showQofGHint = ref(false);
const showInflowHint = ref(false);
const showRevenueHint = ref(false);

const paretoNote = computed(() => {
  const points = store.pareto?.points;
  if (!points?.length) return null;
  const target = points.find((p) => p.customerPct === 18) ?? points[Math.min(2, points.length - 1)];
  return { pct: target.customerPct, cum: target.cumulativePct };
});

const isOverviewLoading = computed(() => store.sectionLoading.overview && !kpi.value);
const overviewError = computed(() => (!kpi.value ? store.sectionErrors.overview || store.error : null));
const rfmState = computed(() => sectionState("rfm", !rfmRows.value.length, "ยังไม่มีข้อมูล RFM Segmentation", "เมื่อมีข้อมูล recency, frequency และ monetary ระบบจะแสดงกลุ่มลูกค้าที่นี่"));
const growthBaseState = computed(() => sectionState("growthBase", !store.growthBase?.labels?.length, "ยังไม่มีข้อมูลการเติบโตของฐานลูกค้า", "ข้อมูลลูกค้าใหม่ ลูกค้าเก่า และอัตรากลับมาทำรายการ ยังไม่พร้อมสำหรับช่วงเวลานี้"));
const behaviorState = computed(() => sectionState("behavior", !behaviorRows.value.length, "ยังไม่มีข้อมูลพฤติกรรมการทำรายการ", "เมื่อมีธุรกรรม ระบบจะแสดงสัดส่วนไถ่ถอน ต่อดอก ลดต้น เพิ่มต้น และหลุด"));
const paretoState = computed(() => sectionState("pareto", !store.pareto?.points?.length, "ยังไม่มีข้อมูลการกระจุกตัวของรายได้", "ข้อมูลดอกเบี้ยตามกลุ่มลูกค้ายังไม่พร้อมสำหรับการแสดงกราฟ Pareto"));
const growthQualityState = computed(() => sectionState("growthQuality", !gq.value?.labels?.length, "ยังไม่มีข้อมูลคุณภาพการเติบโต", "ข้อมูลเงินต้นเปิดใหม่ เงินต้นที่ปิด และเงินไหลเข้ายังไม่พร้อมสำหรับช่วงเวลานี้"));
const revenueBandState = computed(() => sectionState("revenueBand", !store.revenueBand?.labels?.length, "ยังไม่มีข้อมูลที่มาของรายได้", "ข้อมูลดอกเบี้ยแยกตามระดับความเสี่ยงยังไม่พร้อมสำหรับช่วงเวลานี้"));

const conn = computed(() => {
  if (isOverviewLoading.value) return { ok: false, label: "กำลังโหลด" };
  if (overviewError.value) return { ok: false, label: "เชื่อมต่อไม่ได้" };
  if (Object.values(store.sectionErrors).some(Boolean)) return { ok: true, label: "ข้อมูลบางส่วนไม่พร้อม" };
  return { ok: true, label: "เชื่อมต่อแล้ว" };
});

const n = (value) => (value == null ? "-" : Number(value).toLocaleString("th-TH"));
const m = (value) => (value == null ? "-" : (value / 1000000).toFixed(1));
const pctOf = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const arrow = (value) => ((value ?? 0) >= 0 ? "ขึ้น" : "ลง");
const absPct = (value) => Math.abs(value ?? 0);
const signM = (value) => `${value >= 0 ? "+" : "-"}฿${Math.abs(value ?? 0).toFixed(1)}M`;
const signedPct = (value) => `${(value ?? 0) >= 0 ? "+" : "-"}${Math.abs(value ?? 0).toFixed(1)}%`;

function fmtDrillValue(v) {
  if (!v || v <= 0) return "—";
  return `฿${Math.round(v).toLocaleString("th-TH")}`;
}
function sectionState(key, empty, emptyTitle, emptyText) {
  if (store.sectionLoading[key]) {
    return {
      type: "loading",
      icon: "hourglass_top",
      title: "กำลังโหลดข้อมูล...",
      text: "ระบบกำลังดึงข้อมูลส่วนนี้",
    };
  }
  if (store.sectionErrors[key]) {
    return {
      type: "error",
      icon: "report_problem",
      title: "โหลดข้อมูลส่วนนี้ไม่สำเร็จ",
      text: store.sectionErrors[key],
    };
  }
  if (empty) {
    return {
      type: "empty",
      icon: "inbox",
      title: emptyTitle,
      text: emptyText,
    };
  }
  return null;
}

const bandDonut = ref(null);
const paretoChart = ref(null);
const growthChart = ref(null);
const flowChart = ref(null);
const revenueChart = ref(null);
const qualityDonutEls = ref([]);
let bandInst = null;
let paretoInst = null;
let growthInst = null;
let flowInst = null;
let revenueInst = null;
const qualityDonutInsts = [];

function setQualityDonut(el, index) {
  if (el) qualityDonutEls.value[index] = el;
}

const netLabels = {
  id: "netLabels",
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(2);
    if (!meta || meta.hidden) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.font = `700 11px ${APP_FONT}`;
    ctx.fillStyle = C.net;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    meta.data.forEach((point, index) => {
      const ds = chart.data.datasets[2];
      const value = ds.data[index];
      if (value == null) return;
      // รูปแบบตัวเลขมาจาก valueFormat ที่ dataset ประกาศไว้ ไม่ผูกกับชื่อ label
      // เพื่อให้เปลี่ยนป้ายชื่อในกราฟได้โดยที่ % ไม่หายไปเงียบ ๆ
      const label = ds.valueFormat === "percent"
        ? `${value.toFixed(1)}%`
        : (value >= 0 ? "+" : "") + value.toFixed(1);
      ctx.strokeText(label, point.x, point.y - 8);
      ctx.fillText(label, point.x, point.y - 8);
    });
    ctx.restore();
  },
};

// สำหรับ risk band donut — data คือ pct จาก backend อยู่แล้ว (ทศนิยม 1 ตำแหน่ง)
const bandPctLabels = {
  id: "bandPctLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.hidden) return;
    const data = chart.data.datasets[0].data;
    if (!data || data.reduce((s, v) => s + (v || 0), 0) === 0) return;
    ctx.save();
    ctx.font = `700 11px ${APP_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    meta.data.forEach((arc, i) => {
      const pct = data[i] || 0;
      if (pct < 8) return;
      const mid = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;
      const r = (arc.innerRadius + arc.outerRadius) / 2;
      const x = arc.x + Math.cos(mid) * r;
      const y = arc.y + Math.sin(mid) * r;
      ctx.strokeStyle = "rgba(0,0,0,0.25)";
      ctx.lineWidth = 3;
      ctx.lineJoin = "round";
      ctx.strokeText(pct.toFixed(1) + "%", x, y);
      ctx.fillStyle = "#fff";
      ctx.fillText(pct.toFixed(1) + "%", x, y);
    });
    ctx.restore();
  },
};

// สำหรับ inflow/outflow donuts — data คือค่าเงิน (฿M) ต้องคำนวณ % เอง
// ใช้ largest-remainder method เพื่อให้ผลรวม = 100% เสมอ
const donutPctLabels = {
  id: "donutPctLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.hidden) return;
    const data = chart.data.datasets[0].data;
    const total = (data || []).reduce((s, v) => s + (v || 0), 0);
    if (total === 0) return;
    const raw = data.map((v) => ((v || 0) / total) * 100);
    const floored = raw.map(Math.floor);
    const remainder = 100 - floored.reduce((s, v) => s + v, 0);
    const indices = raw.map((_, i) => i).sort((a, b) => (raw[b] - floored[b]) - (raw[a] - floored[a]));
    for (let k = 0; k < remainder; k++) floored[indices[k]]++;
    ctx.save();
    ctx.font = `700 11px ${APP_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    meta.data.forEach((arc, i) => {
      const pct = floored[i];
      if (!pct || pct < 8) return;
      const mid = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;
      const r = (arc.innerRadius + arc.outerRadius) / 2;
      const x = arc.x + Math.cos(mid) * r;
      const y = arc.y + Math.sin(mid) * r;
      ctx.strokeStyle = "rgba(0,0,0,0.25)";
      ctx.lineWidth = 3;
      ctx.lineJoin = "round";
      ctx.strokeText(pct + "%", x, y);
      ctx.fillStyle = "#fff";
      ctx.fillText(pct + "%", x, y);
    });
    ctx.restore();
  },
};

// Label each stacked revenue-band segment. % mode (no line dataset) → "NN%", amount mode → "N.N".
const revSegLabels = {
  id: "revSegLabels",
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    const isPct = !chart.data.datasets.some((d) => d.type === "line");
    ctx.save();
    ctx.font = `700 10px ${APP_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    chart.data.datasets.forEach((ds, di) => {
      if (ds.type === "line") return;
      const meta = chart.getDatasetMeta(di);
      if (!meta || meta.hidden) return;
      meta.data.forEach((bar, i) => {
        const v = ds.data[i];
        if (!v) return;
        const base = bar.base ?? bar.y;
        const h = Math.abs(base - bar.y);
        if (h < 15) return;
        const label = isPct ? `${Number(v).toFixed(1)}%` : Number(v).toFixed(2);
        const cy = (bar.y + base) / 2;
        ctx.strokeStyle = "rgba(0,0,0,0.28)";
        ctx.lineWidth = 3;
        ctx.lineJoin = "round";
        ctx.strokeText(label, bar.x, cy);
        ctx.fillStyle = "#fff";
        ctx.fillText(label, bar.x, cy);
      });
    });
    ctx.restore();
  },
};

// Total-revenue line value labels (amount mode only — pct mode has no line dataset).
const revTotalLabel = {
  id: "revTotalLabel",
  afterDatasetsDraw(chart) {
    const di = chart.data.datasets.findIndex((d) => d.type === "line");
    if (di < 0) return;
    const meta = chart.getDatasetMeta(di);
    if (!meta || meta.hidden) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.font = `700 11px ${APP_FONT}`;
    ctx.fillStyle = C.net;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    meta.data.forEach((pt, i) => {
      const v = chart.data.datasets[di].data[i];
      if (v == null) return;
      const label = `฿${Number(v).toFixed(2)}M`;
      ctx.strokeText(label, pt.x, pt.y - 8);
      ctx.fillText(label, pt.x, pt.y - 8);
    });
    ctx.restore();
  },
};

const paretoLabels = {
  id: "paretoLabels",
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(1);
    if (!meta || meta.hidden) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.font = `700 11px ${APP_FONT}`;
    ctx.fillStyle = C.accent;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.textAlign = "center";
    const chartTop = chart.chartArea.top;
    meta.data.forEach((point, index) => {
      const value = chart.data.datasets[1].data[index];
      if (value == null) return;
      const previous = chart.data.datasets[1].data[index - 1];
      const isLastPoint = index === chart.data.datasets[1].data.length - 1;
      if (previous != null && Math.round(previous) === Math.round(value) && !isLastPoint) return;

      const label = `${value.toFixed(0)}%`;
      const isNearTop = point.y - chartTop < 24;
      const labelY = isNearTop ? point.y + 12 : point.y - 8;
      ctx.textBaseline = isNearTop ? "top" : "bottom";
      ctx.strokeText(label, point.x, labelY);
      ctx.fillText(label, point.x, labelY);
    });
    ctx.restore();
  },
};

function chartDefaults() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { boxWidth: 12, padding: 14, color: "#67748e" } },
      tooltip: {
        titleFont: { family: APP_FONT },
        bodyFont: { family: APP_FONT },
        footerFont: { family: APP_FONT },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#67748e" } },
      y: { grid: { color: C.line }, ticks: { color: "#67748e" } },
    },
  };
}

function clearExternalTooltip(chart) {
  if (chart?.$insightTooltipEl) {
    chart.$insightTooltipEl.remove();
    chart.$insightTooltipEl = null;
  }
}

function externalDonutTooltip({ chart, tooltip }) {
  let tooltipEl = chart.$insightTooltipEl;
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className = "insight-chart-tooltip";
    document.body.appendChild(tooltipEl);
    chart.$insightTooltipEl = tooltipEl;
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  const title = tooltip.title?.[0] ?? "";
  const line = tooltip.body?.[0]?.lines?.[0] ?? "";
  tooltipEl.replaceChildren();

  if (title) {
    const titleEl = document.createElement("div");
    titleEl.className = "insight-chart-tooltip-title";
    titleEl.textContent = title;
    tooltipEl.appendChild(titleEl);
  }

  const bodyEl = document.createElement("div");
  bodyEl.className = "insight-chart-tooltip-body";
  bodyEl.textContent = line;
  tooltipEl.appendChild(bodyEl);

  const rect = chart.canvas.getBoundingClientRect();
  const left = rect.left + window.pageXOffset + tooltip.caretX;
  const top = rect.top + window.pageYOffset + tooltip.caretY;
  tooltipEl.style.opacity = "1";
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
}

function renderOverviewCharts() {
  if (bandDonut.value) {
    bandInst?.destroy();
    bandInst = new Chart(bandDonut.value, {
      type: "doughnut",
      data: {
        labels: ["เสี่ยงต่ำ", "ปานกลาง", "เสี่ยงสูง"],
        datasets: [{ data: bands.value.map((b) => b.pct), backgroundColor: [C.good, C.warn, C.bad], borderWidth: 0, cutout: "68%" }],
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
      plugins: [bandPctLabels],
    });
  }

  if (paretoChart.value) {
    const points = store.pareto?.points ?? [];
    paretoInst?.destroy();
    paretoInst = new Chart(paretoChart.value, {
      data: {
        labels: points.map((p) => `Top ${p.customerPct}%`),
        datasets: [
          { type: "bar", label: "ดอกเบี้ย (ล้านบาท)", data: points.map((p) => p.interest), backgroundColor: C.gold, borderRadius: 5, yAxisID: "y", order: 2 },
          {
            type: "line",
            label: "สะสม %",
            data: points.map((p) => p.cumulativePct),
            borderColor: C.accent,
            backgroundColor: "transparent",
            pointBackgroundColor: "#fff",
            pointBorderColor: C.accent,
            pointBorderWidth: 2.5,
            borderWidth: 3.4,
            tension: 0.35,
            pointRadius: 4.5,
            pointHoverRadius: 6,
            pointHitRadius: 10,
            clip: false,
            yAxisID: "y1",
            order: 1,
          },
        ],
      },
      options: {
        ...chartDefaults(),
        layout: { padding: { top: 30, right: 8 } },
        plugins: {
          legend: {
            labels: {
              boxWidth: 11,
              padding: 18,
              usePointStyle: true,
              pointStyle: "rectRounded",
              color: "#67748e",
            },
          },
        },
        scales: {
          y: { grid: { color: C.line }, ticks: { color: "#67748e", callback: (value) => `${Math.round(value * 10) / 10}M` } },
          y1: { position: "right", min: 0, max: 100, grid: { display: false }, ticks: { color: "#67748e", callback: (value) => `${Math.round(value)}%` } },
          x: { grid: { display: false }, ticks: { color: "#67748e" } },
        },
      },
      plugins: [paretoLabels],
    });
  }
}

function renderGrowth() {
  if (!growthChart.value || !store.growthBase) return;
  const growth = store.growthBase;
  growthInst?.destroy();
  growthInst = new Chart(growthChart.value, {
    data: {
      labels: growth.labels,
      datasets: [
        { type: "bar", label: "ลูกค้าใหม่", data: growth.newCustomers, backgroundColor: C.purple, borderRadius: 4, stack: "customers", order: 2 },
        { type: "bar", label: "ลูกค้าเก่า", data: growth.returning, backgroundColor: C.good, borderRadius: 4, stack: "customers", order: 2 },
        {
          type: "line",
          label: "อัตรากลับมาทำรายการ",
          valueFormat: "percent",
          data: growth.retentionPct,
          borderColor: C.net,
          backgroundColor: "transparent",
          pointBackgroundColor: "#fff",
          pointBorderColor: C.net,
          pointBorderWidth: 2.5,
          borderWidth: 3.4,
          tension: 0.35,
          pointRadius: 4.5,
          pointHoverRadius: 6,
          pointHitRadius: 10,
          yAxisID: "y1",
          order: 1,
        },
      ],
    },
    options: {
      ...chartDefaults(),
      layout: { padding: { top: 22 } },
      scales: {
        y: { stacked: true, grid: { color: C.line }, ticks: { color: "#67748e" } },
        y1: { position: "right", min: 0, max: 118, grid: { display: false }, ticks: { stepSize: 20, color: "#67748e", callback: (value) => (value <= 100 ? `${value}%` : "") } },
        x: { stacked: true, grid: { display: false }, ticks: { color: "#67748e" } },
      },
    },
    plugins: [netLabels],
  });
}

function renderQuality() {
  const growth = store.growthQuality;
  if (!growth) return;

  if (flowChart.value) {
    flowInst?.destroy();
    flowInst = new Chart(flowChart.value, {
      type: "bar",
      data: {
        labels: growth.labels,
        datasets: [
          { label: "เงินต้นเปิดใหม่", data: inflowTotal.value, backgroundColor: C.good, borderRadius: 5, order: 2 },
          { label: "เงินต้นที่ปิด", data: growth.labels.map((_, i) => (growth.outflowRedeem[i] ?? 0) + (growth.outflowReduce[i] ?? 0) + (growth.outflowForfeit[i] ?? 0)), backgroundColor: C.bad, borderRadius: 5, order: 2 },
          {
            type: "line",
            label: "เติบโตสุทธิ",
            valueFormat: "signed",
            data: growth.net,
            borderColor: C.net,
            backgroundColor: "transparent",
            pointBackgroundColor: "#fff",
            pointBorderColor: C.net,
            pointBorderWidth: 2.5,
            borderWidth: 3.4,
            tension: 0.3,
            pointRadius: 4.5,
            pointHoverRadius: 6,
            pointHitRadius: 10,
            order: 1,
          },
        ],
      },
      options: {
        ...chartDefaults(),
        layout: { padding: { top: 22 } },
        plugins: {
          legend: { labels: { boxWidth: 11, padding: 12, usePointStyle: true, pointStyle: "rectRounded", color: "#67748e" } },
          tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ฿${Math.abs(context.raw).toFixed(1)}M` } },
        },
        scales: {
          y: { grid: { color: C.line }, beginAtZero: true, ticks: { color: "#67748e", callback: (value) => `${Math.round(value * 10) / 10}M` } },
          x: { grid: { display: false }, ticks: { color: "#67748e" } },
        },
      },
      plugins: [netLabels],
    });
  }

  donutData.value.forEach((data, index) => {
    const el = qualityDonutEls.value[index];
    if (!el) return;
    clearExternalTooltip(qualityDonutInsts[index]);
    qualityDonutInsts[index]?.destroy();
    qualityDonutInsts[index] = new Chart(el, {
      type: "doughnut",
      data: {
        labels: ["ลูกค้าใหม่จริง", "ลูกค้าเก่า — จำนำใหม่", "เพิ่มต้น — ตั๋วเดิม"],
        datasets: [{ data: data.data, backgroundColor: ["#3b82f6", "#10b981", "#8b5cf6"], borderWidth: 0, cutout: "66%" }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_, elements) => {
          if (!elements.length) return;
          handleDrilldown("inflow", index, elements[0].index, data.startDate, data.endDate, data.label);
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: externalDonutTooltip,
            callbacks: { label: (context) => `${context.label}: ฿${context.raw.toFixed(1)}M` },
          },
        },
      },
      plugins: [donutPctLabels],
    });
  });

  outflowDonutData.value.forEach((data, index) => {
    const el = outflowDonutEls.value[index];
    if (!el) return;
    clearExternalTooltip(outflowDonutInsts[index]);
    outflowDonutInsts[index]?.destroy();
    outflowDonutInsts[index] = new Chart(el, {
      type: "doughnut",
      data: {
        labels: ["ไถ่ถอน", "ลดต้น", "หลุด"],
        datasets: [{ data: data.data, backgroundColor: ["#f43f5e", "#f59e0b", "#d946ef"], borderWidth: 0, cutout: "66%" }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_, elements) => {
          if (!elements.length) return;
          handleDrilldown("outflow", index, elements[0].index, data.startDate, data.endDate, data.label);
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: externalDonutTooltip,
            callbacks: { label: (context) => `${context.label}: ฿${context.raw.toFixed(1)}M` },
          },
        },
      },
      plugins: [donutPctLabels],
    });
  });
}

function renderRevenue() {
  if (!revenueChart.value || !store.revenueBand) return;
  const rb = store.revenueBand;
  revenueInst?.destroy();

  const isPct = revenueMode.value === "pct";
  const toPct = (arr) =>
    rb.labels.map((_, i) => {
      const t = rb.total[i] || 0;
      return t > 0 ? Math.round(((arr[i] ?? 0) / t) * 1000) / 10 : 0;
    });
  const low = isPct ? toPct(rb.low) : rb.low;
  const mid = isPct ? toPct(rb.mid) : rb.mid;
  const high = isPct ? toPct(rb.high) : rb.high;
  // headroom so the total-line peak (amount mode) clears the legend + value label
  const amountMax = Math.max(0, ...rb.total) * 1.18;

  const datasets = [
    { type: "bar", label: "เสี่ยงต่ำ", data: low, backgroundColor: C.good, borderRadius: 4, stack: "rev", order: 2 },
    { type: "bar", label: "ปานกลาง", data: mid, backgroundColor: C.warn, borderRadius: 4, stack: "rev", order: 2 },
    { type: "bar", label: "เสี่ยงสูง", data: high, backgroundColor: C.bad, borderRadius: 4, stack: "rev", order: 2 },
  ];
  if (!isPct) {
    datasets.push({
      type: "line",
      label: "ดอกเบี้ยรวม",
      data: rb.total,
      borderColor: C.net,
      backgroundColor: "transparent",
      pointBackgroundColor: "#fff",
      pointBorderColor: C.net,
      pointBorderWidth: 2.5,
      borderWidth: 3.4,
      tension: 0.35,
      pointRadius: 4.5,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      order: 1,
    });
  }

  revenueInst = new Chart(revenueChart.value, {
    data: { labels: rb.labels, datasets },
    options: {
      ...chartDefaults(),
      layout: { padding: { top: 22 } },
      plugins: {
        legend: { labels: { boxWidth: 11, padding: 12, usePointStyle: true, pointStyle: "rectRounded", color: "#67748e" } },
        tooltip: {
          callbacks: {
            label: (context) =>
              isPct
                ? `${context.dataset.label}: ${context.raw}%`
                : `${context.dataset.label}: ฿${Number(context.raw).toFixed(2)}M`,
          },
        },
      },
      scales: {
        y: {
          stacked: true,
          beginAtZero: true,
          ...(isPct ? { max: 100 } : { suggestedMax: amountMax }),
          grid: { color: C.line },
          ticks: { color: "#67748e", callback: (value) => (isPct ? `${Math.round(value)}%` : `${Math.round(value * 10) / 10}M`) },
        },
        x: { stacked: true, grid: { display: false }, ticks: { color: "#67748e" } },
      },
    },
    plugins: [revSegLabels, revTotalLabel],
  });
}

function destroyCharts() {
  bandInst?.destroy();
  paretoInst?.destroy();
  growthInst?.destroy();
  flowInst?.destroy();
  revenueInst?.destroy();
  qualityDonutInsts.forEach((chart) => {
    clearExternalTooltip(chart);
    chart?.destroy();
  });
  outflowDonutInsts.forEach((chart) => {
    clearExternalTooltip(chart);
    chart?.destroy();
  });
}

watch(
  () => store.overview,
  async () => {
    await nextTick();
    renderOverviewCharts();
  }
);
watch(
  () => store.pareto,
  async () => {
    await nextTick();
    renderOverviewCharts();
  }
);
watch(
  () => store.growthBase,
  async () => {
    await nextTick();
    renderGrowth();
  }
);
watch(
  () => store.growthQuality,
  async () => {
    await nextTick();
    renderQuality();
  }
);
watch(
  () => store.revenueBand,
  async () => {
    await nextTick();
    renderRevenue();
  }
);

// When the main content gate opens (kpi ready → canvases finally mount), render every chart
// whose data already arrived. Fixes the initial-load race where a section resolves before
// overview, fires its watcher while the canvas is still hidden, and never re-renders.
watch(
  () => !!kpi.value,
  async (ready) => {
    if (!ready) return;
    await nextTick();
    renderOverviewCharts();
    renderGrowth();
    renderQuality();
    renderRevenue();
  },
  { immediate: true }
);

onMounted(() => {
  store.loadDashboard();
  loadLineCustomerCount();
});
onBeforeUnmount(destroyCharts);
</script>

<style scoped>
.insight-page {
  width: 100%;
  padding: 16px 24px;
  font-family: var(--bs-body-font-family, "THSarabunNew", "Sukhumvit Set", "Prompt", "Tahoma", "Roboto", sans-serif);
  font-weight: 400;
  line-height: 1.5;
}

.insight-page button,
.insight-page input,
.insight-page select,
.insight-page textarea {
  font-family: inherit;
}

.insight-page h2,
.insight-page h3 {
  font-family: inherit;
}

.page-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.eyebrow {
  margin: 0 0 4px;
  color: #7b809a;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.page-title h2 {
  margin: 0;
  color: #344767;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
}

.subtitle {
  margin: 4px 0 0;
  color: #7b809a;
  font-size: 0.9rem;
  font-weight: 300;
  line-height: 1.625;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 8px 13px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  color: #7b809a;
  font-size: 0.82rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.status-pill .material-icons-round {
  font-size: 18px;
}

.status-pill.ok .material-icons-round {
  color: #1faa5e;
}

.status-pill.bad .material-icons-round {
  color: #ef4444;
}

.state-box {
  text-align: center;
  padding: 76px 24px;
  color: #7b809a;
}

.state-box .material-icons-round {
  font-size: 42px;
  color: #344767;
  margin-bottom: 10px;
}

.state-box h3 {
  margin: 0;
  color: #344767;
  font-size: 1.15rem;
}

.state-box p {
  margin-top: 8px;
  font-weight: 300;
  line-height: 1.625;
}

.state-box.error .material-icons-round,
.state-box.error h3 {
  color: #ef4444;
}

.section-state {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 5px;
  min-height: 150px;
  padding: 18px;
  border: 1px dashed #d9dee8;
  border-radius: 8px;
  background: #f8f9fa;
  color: #7b809a;
  text-align: center;
}

.section-state .material-icons-round {
  color: #7b809a;
  font-size: 28px;
}

.section-state strong {
  color: #344767;
  font-size: 0.86rem;
  font-weight: 800;
}

.section-state p {
  max-width: 420px;
  margin: 0;
  color: #7b809a;
  font-size: 0.76rem;
  font-weight: 400;
  line-height: 1.55;
}

.section-state.loading {
  border-color: rgba(13, 110, 253, 0.18);
  background: rgba(13, 110, 253, 0.045);
}

.section-state.loading .material-icons-round {
  color: #0d6efd;
}

.section-state.error {
  border-color: rgba(239, 68, 68, 0.22);
  background: rgba(239, 68, 68, 0.055);
}

.section-state.error .material-icons-round,
.section-state.error strong {
  color: #ef4444;
}

.chart-placeholder {
  min-height: 230px;
  flex: 1;
}

.flow-placeholder {
  min-height: 360px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 18px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.035);
  min-width: 0;
}

.kpi-card {
  position: relative;
  min-height: 136px;
}

.kpi-icon {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: #fff;
}

.kpi-icon.primary {
  background: linear-gradient(195deg, #42424a, #191919);
}

.kpi-icon.success {
  background: linear-gradient(195deg, #66bb6a, #43a047);
}

.kpi-icon.info {
  background: linear-gradient(195deg, #49a3f1, #1a73e8);
}

.kpi-icon.danger {
  background: linear-gradient(195deg, #ef5350, #e53935);
}

.kpi-label {
  color: #7b809a;
  font-size: 0.82rem;
  font-weight: 700;
  padding-right: 48px;
}

.kpi-value {
  margin-top: 16px;
  color: #344767;
  font-size: 1.75rem;
  font-weight: 800;
}

.kpi-sub {
  display: inline-flex;
  margin-top: 8px;
  padding: 4px 8px;
  background: rgba(31, 170, 94, 0.1);
  border-radius: 999px;
  color: #1faa5e;
  font-size: 0.74rem;
  font-weight: 700;
}

.kpi-sub.down {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.alert-board {
  margin-top: 14px;
  padding: 18px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.035);
}

.alert-board-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.alert-board-head h3 {
  margin: 0;
  color: #344767;
  font-size: 0.95rem;
  font-weight: 800;
}

.alert-board-head p {
  margin: 4px 0 0;
  color: #7b809a;
  font-size: 0.78rem;
  font-weight: 300;
}

.alert-board-badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(13, 110, 253, 0.08);
  color: #0d6efd;
  font-size: 0.74rem;
  font-weight: 800;
  white-space: nowrap;
}

.alert-board-badge.success {
  background: rgba(31, 170, 94, 0.1);
  color: #1faa5e;
}

.alert-board-badge.warning {
  background: rgba(245, 158, 11, 0.12);
  color: #d98a05;
}

.alert-board-badge.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.alert-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.alert-card {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
  min-height: 104px;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
}

.alert-icon {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #fff;
}

.alert-icon .material-icons-round {
  font-size: 20px;
}

.alert-card strong {
  display: block;
  color: #344767;
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.35;
}

.alert-card p {
  margin: 4px 0 0;
  color: #7b809a;
  font-size: 0.74rem;
  font-weight: 400;
  line-height: 1.5;
}

.alert-card.success {
  border-color: rgba(31, 170, 94, 0.16);
  background: rgba(31, 170, 94, 0.055);
}

.alert-card.success .alert-icon {
  color: #1faa5e;
}

.alert-card.info {
  border-color: rgba(13, 110, 253, 0.16);
  background: rgba(13, 110, 253, 0.045);
}

.alert-card.info .alert-icon {
  color: #0d6efd;
}

.alert-card.warning {
  border-color: rgba(245, 158, 11, 0.2);
  background: rgba(245, 158, 11, 0.065);
}

.alert-card.warning .alert-icon {
  color: #d98a05;
}

.alert-card.danger {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.055);
}

.alert-card.danger .alert-icon {
  color: #ef4444;
}

.revenue-risk-card {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid rgba(13, 110, 253, 0.16);
  border-radius: 8px;
  background: rgba(13, 110, 253, 0.045);
}

.revenue-risk-main {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
  min-width: 0;
}

.revenue-risk-icon {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #fff;
  color: #0d6efd;
}

.revenue-risk-main span,
.revenue-risk-stats span {
  display: block;
  color: #7b809a;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.35;
}

.revenue-risk-main strong {
  display: block;
  margin-top: 2px;
  color: #344767;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.35;
}

.revenue-risk-main p {
  margin: 4px 0 0;
  color: #7b809a;
  font-size: 0.76rem;
  font-weight: 400;
  line-height: 1.55;
}

.revenue-risk-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}

.revenue-risk-stats > div {
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.74);
  min-width: 0;
}

.revenue-risk-stats strong {
  display: block;
  margin-top: 5px;
  color: #344767;
  font-size: 1rem;
  font-weight: 900;
}

.revenue-risk-stats strong.down {
  color: #1faa5e;
}

.revenue-risk-card.success {
  border-color: rgba(31, 170, 94, 0.16);
  background: rgba(31, 170, 94, 0.055);
}

.revenue-risk-card.success .revenue-risk-icon {
  color: #1faa5e;
}

.revenue-risk-card.warning {
  border-color: rgba(245, 158, 11, 0.2);
  background: rgba(245, 158, 11, 0.065);
}

.revenue-risk-card.warning .revenue-risk-icon {
  color: #d98a05;
}

.revenue-risk-card.danger {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.055);
}

.revenue-risk-card.danger .revenue-risk-icon,
.revenue-risk-card.danger .revenue-risk-stats strong {
  color: #ef4444;
}

.section-title {
  margin: 26px 4px 12px;
  color: #7b809a;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.two-col {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 1fr);
  align-items: stretch;
  gap: 14px;
  margin-bottom: 16px;
}

.portfolio-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.portfolio-unified-card {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.asset-drilldown {
  margin-top: 20px;
  border-top: 1px solid #e9ecef;
  padding-top: 16px;
}

.asset-drilldown-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;

  strong {
    font-size: 14px;
    font-weight: 700;
    color: #344767;
    flex: 1;
  }
}

.drill-period-badge {
  font-size: 12px;
  font-weight: 600;
  color: #67748e;
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 10px;
}

.drill-close {
  border: none;
  background: none;
  cursor: pointer;
  color: #9fa5b1;
  display: flex;
  align-items: center;
  padding: 2px;

  &:hover { color: #344767; }

  .material-icons-round { font-size: 18px; }
}

.drill-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9fa5b1;
  font-size: 13px;
  padding: 12px 0;
}

.drill-bars {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.drill-row {
  display: grid;
  grid-template-columns: 120px 1fr 48px auto auto;
  align-items: center;
  column-gap: 8px;
}

.drill-label {
  font-size: 13px;
  font-weight: 600;
  color: #344767;
  text-align: right;
}

.drill-bar-wrap {
  height: 10px;
  background: #f0f2f5;
  border-radius: 5px;
  overflow: hidden;
}

.drill-bar {
  height: 100%;
  border-radius: 5px;
  transition: width 0.4s ease;
  min-width: 2px;
}

.drill-pct {
  font-size: 13px;
  font-weight: 700;
  color: #344767;
  text-align: right;
}

.drill-count {
  font-size: 12px;
  color: #9fa5b1;
}

.drill-value {
  font-size: 12px;
  font-weight: 600;
  color: #344767;
  text-align: right;
}

.drill-note {
  font-size: 11px;
  color: #9fa5b1;
  margin-top: 6px;
  font-style: italic;
}

.drill-slide-enter-active,
.drill-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.drill-slide-enter-from,
.drill-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.portfolio-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 20px 0 16px;
  color: #9fa5b1;
  font-size: 13px;
  font-weight: 600;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e9ecef;
  }
}

.portfolio-flow-detail {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: start;
}

.portfolio-vsep {
  width: 1px;
  background: #e9ecef;
  align-self: stretch;
  margin: 0 20px;
}

.portfolio-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.portfolio-sub-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;

  h4 {
    font-size: 15px;
    font-weight: 700;
    color: #344767;
    margin: 0 0 2px;
  }

  p {
    font-size: 13px;
    color: #9fa5b1;
    margin: 0;
  }
}

.two-col > .card,
.portfolio-grid > .card {
  height: 100%;
}

.card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.card-head h3 {
  margin: 0;
  color: #344767;
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1.375;
}

.card-head p {
  margin: 4px 0 0;
  color: #7b809a;
  font-size: 0.78rem;
  font-weight: 300;
  line-height: 1.625;
}

.matrix {
  display: grid;
  grid-template-columns: auto repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.axis-y {
  align-self: center;
  color: #7b809a;
  font-size: 0.72rem;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
}

.matrix-cell {
  position: relative;
  min-height: 78px;
  padding: 11px 10px;
  border: 1px solid #e9ecef;
  border-left-width: 4px;
  border-radius: 8px;
  cursor: default;
}

.cell-tip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  z-index: 50;
  width: max-content;
  max-width: 230px;
  padding: 10px 12px;
  background: rgba(52, 71, 103, 0.97);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(52, 71, 103, 0.22);
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, 4px);
  transition: opacity 0.14s ease, transform 0.14s ease;
  pointer-events: none;
}

.matrix-cell:hover .cell-tip {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}

.cell-tip-name {
  color: #fff;
  font-size: 0.78rem;
  font-weight: 800;
  margin-bottom: 6px;
}

.cell-tip-row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  font-size: 0.72rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.72);
}

.cell-tip-row b {
  color: #fff;
  font-weight: 700;
  white-space: nowrap;
}

.cell-name {
  color: #344767;
  font-size: 0.74rem;
  font-weight: 800;
}

.cell-count {
  margin-top: 6px;
  color: var(--cc);
  font-size: 1.35rem;
  font-weight: 800;
}

.cell-pct {
  color: #7b809a;
  font-size: 0.7rem;
}

.c-plat {
  background: #e8f6f3;
  border-left-color: #14a08a;
  --cc: #0e7a69;
}

.c-gold {
  background: #fbf3e0;
  border-left-color: #c79320;
  --cc: #9c7416;
}

.c-silv {
  background: #eef2f7;
  border-left-color: #7e8ea0;
  --cc: #5d6b7a;
}

.c-watch {
  background: #e7f1fb;
  border-left-color: #3b82d6;
  --cc: #2868b0;
}

.c-pref {
  background: #ecedfb;
  border-left-color: #6a6fd6;
  --cc: #4a4fb0;
}

.c-std {
  background: #eef0f3;
  border-left-color: #8a93a3;
  --cc: #5d6675;
}

.c-mng {
  background: #fbeede;
  border-left-color: #d3842a;
  --cc: #a8631a;
}

.c-grd {
  background: #fbe9e7;
  border-left-color: #d9594f;
  --cc: #b03e35;
}

.c-cau {
  background: #fbe3e1;
  border-left-color: #cf3a30;
  --cc: #a82c23;
}

.axis-x {
  display: grid;
  grid-template-columns: auto repeat(3, minmax(0, 1fr));
  gap: 7px;
  margin-top: 8px;
}

.axis-x span {
  text-align: center;
  color: #7b809a;
  font-size: 0.72rem;
}

.risk-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-box {
  position: relative;
  height: 230px;
  min-width: 0;
}

.chart-box canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-box.donut {
  width: 180px;
  flex: 0 0 180px;
}

.chart-box.tall {
  height: 270px;
}

.chart-box.flow {
  height: 310px;
}

.bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.bar-row {
  display: grid;
  grid-template-columns: 128px minmax(0, 1fr) 42px;
  align-items: center;
  gap: 10px;
}

.behavior-note {
  margin: 4px 0 0;
  color: #adb5bd;
  font-size: 0.68rem;
}

.bar-name {
  color: #344767;
  font-size: 0.78rem;
  font-weight: 700;
}

.bar-name small {
  display: block;
  color: #7b809a;
  font-size: 0.68rem;
  font-weight: 600;
}

.track {
  height: 9px;
  background: #f0f2f5;
  border-radius: 999px;
  overflow: hidden;
}

.fill {
  height: 100%;
  border-radius: 999px;
}

.bar-value {
  color: #344767;
  text-align: right;
  font-size: 0.78rem;
  font-weight: 800;
}

.inline-note {
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px dashed #e9ecef;
  color: #7b809a;
  font-size: 0.76rem;
  line-height: 1.55;
}

.inline-note strong {
  color: #1faa5e;
}

.segment-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
}

.segment-card {
  position: relative;
  overflow: hidden;
  min-height: 132px;
  padding: 14px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
}

.segment-card::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: var(--tone);
}

.segment-card .material-icons-round {
  color: var(--tone);
  font-size: 22px;
}

.seg-name {
  margin-top: 8px;
  color: #344767;
  font-size: 0.82rem;
  font-weight: 800;
}

.seg-count {
  margin-top: 4px;
  color: #344767;
  font-size: 1.25rem;
  font-weight: 800;
}

.seg-desc {
  margin-top: 4px;
  color: #7b809a;
  font-size: 0.7rem;
  line-height: 1.45;
}

.insight-page > .chart-card {
  margin-bottom: 16px;
}

.chart-card .inline-note {
  margin-top: auto;
}

.period-toggle {
  display: inline-flex;
  gap: 3px;
  padding: 3px;
  background: #f0f2f5;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.period-toggle button {
  border: 0;
  background: transparent;
  border-radius: 6px;
  color: #7b809a;
  cursor: pointer;
  font-size: 0.76rem;
  font-weight: 800;
  padding: 7px 12px;
}

.period-toggle button.active {
  background: #344767;
  color: #fff;
}

.toggle-with-month {
  display: flex;
  align-items: center;
  gap: 8px;
}

.period-month-input {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f0f2f5;
  color: #344767;
  font-size: 0.76rem;
  font-weight: 800;
  padding: 6px 10px;
  cursor: pointer;
}

.rev-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.mini-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.mini-stat {
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.mini-stat span {
  display: block;
  color: #7b809a;
  font-size: 0.73rem;
  font-weight: 700;
}

.mini-stat strong {
  display: block;
  margin-top: 4px;
  color: #344767;
  font-size: 1.16rem;
}

.mini-stat.good strong {
  color: #1faa5e;
}

.mini-stat.primary strong {
  color: #0d6efd;
}

.mini-stat.danger strong {
  color: #ef4444;
}

.growth-readout {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  margin: 0 0 14px;
  padding: 11px 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
}

.growth-readout > .material-icons-round {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #fff;
  font-size: 20px;
}

.growth-readout strong {
  display: block;
  color: #344767;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.35;
}

.growth-readout p {
  margin: 2px 0 0;
  color: #7b809a;
  font-size: 0.74rem;
  font-weight: 400;
  line-height: 1.45;
}

.readout-hint {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 5px !important;
  color: #adb5bd !important;
  font-size: 0.68rem !important;
}

.readout-hint .material-icons-round {
  font-size: 13px;
}

.growth-chips {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.growth-chips span {
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: #fff;
  color: #344767;
  font-size: 0.7rem;
  font-weight: 800;
}

.growth-readout.success {
  border-color: rgba(31, 170, 94, 0.16);
  background: rgba(31, 170, 94, 0.06);
}

.growth-readout.success > .material-icons-round {
  color: #1faa5e;
}

.growth-readout.info {
  border-color: rgba(13, 110, 253, 0.16);
  background: rgba(13, 110, 253, 0.055);
}

.growth-readout.info > .material-icons-round {
  color: #0d6efd;
}

.growth-readout.warning {
  border-color: rgba(245, 158, 11, 0.2);
  background: rgba(245, 158, 11, 0.07);
}

.growth-readout.warning > .material-icons-round {
  color: #d98a05;
}

.growth-readout.danger {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.06);
}

.growth-readout.danger > .material-icons-round {
  color: #ef4444;
}

.composition-card .card-head {
  margin-bottom: 0;
}

.composition-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  min-height: 0;
  padding: 6px 0;
}

.composition-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.composition {
  text-align: center;
}

.comp-label {
  color: #7b809a;
  font-size: 0.75rem;
  font-weight: 700;
}

.comp-label.current {
  color: #344767;
}

.comp-donut {
  height: 138px;
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
}

.donut-center strong {
  display: block;
  color: #7c5cff;
  font-size: 1rem;
  line-height: 1;
}

.donut-center span {
  display: block;
  margin-top: 2px;
  color: #7b809a;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
}

:global(.insight-chart-tooltip) {
  position: absolute;
  z-index: 1080;
  max-width: calc(100vw - 24px);
  padding: 8px 10px;
  background: rgba(52, 71, 103, 0.96);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(52, 71, 103, 0.2);
  color: #fff;
  font-family: var(--bs-body-font-family, "THSarabunNew", "Sukhumvit Set", "Prompt", "Tahoma", "Roboto", sans-serif);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.45;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 10px));
  transition: opacity 0.12s ease;
  white-space: nowrap;
}

:global(.insight-chart-tooltip-title) {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.7rem;
  font-weight: 700;
}

:global(.insight-chart-tooltip-body) {
  color: #fff;
}

.comp-total {
  color: #7b809a;
  font-size: 0.7rem;
}

.legend {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 0;
}

.legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #7b809a;
  font-size: 0.72rem;
  font-weight: 700;
}

.legend i {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.legend .new {
  background: #3b82f6;
}

.legend .returning {
  background: #10b981;
}

.legend .increase {
  background: #8b5cf6;
}

.legend .redeem {
  background: #f43f5e;
}

.legend .reduce-out {
  background: #f59e0b;
}

.legend .forfeit {
  background: #d946ef;
}

.insight-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 0;
  padding: 12px;
  border: 1px solid rgba(13, 110, 253, 0.18);
  border-radius: 8px;
  background: rgba(13, 110, 253, 0.06);
}

.insight-note .material-icons-round {
  color: #0d6efd;
  font-size: 18px;
}

.insight-note p {
  margin: 0;
  color: #344767;
  font-size: 0.76rem;
  font-weight: 300;
  line-height: 1.55;
}

/* ── Hint button + popover ────────────────────────────────────────── */
.card-head-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.hint-wrap {
  position: relative;
  flex-shrink: 0;
  margin-top: 2px;
}

.hint-btn {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  color: #7b809a;
  transition: all 0.15s;
  padding: 0;
}

.hint-btn .material-icons-round {
  font-size: 16px;
}

.hint-btn:hover,
.hint-btn.on {
  border-color: #0d6efd;
  color: #0d6efd;
  background: rgba(13, 110, 253, 0.07);
}

.hint-pop {
  position: absolute;
  top: 32px;
  left: 0;
  z-index: 200;
}

.hint-pop-left {
  left: auto;
  right: 0;
  width: 290px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(52, 71, 103, 0.13);
  padding: 14px 16px;
}

.hp-title {
  font-size: 0.68rem;
  font-weight: 800;
  color: #7b809a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.hp-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.76rem;
  color: #344767;
  line-height: 1.5;
}

.hp-sq {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  flex-shrink: 0;
  margin-top: 2px;
}

.hp-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
}

.hp-sep {
  border-top: 1px dashed #e9ecef;
  margin: 10px 0;
}

.hp-body {
  font-size: 0.76rem;
  color: #344767;
  line-height: 1.6;
  margin-bottom: 2px;
  font-weight: 300;
}

.hp-level {
  display: grid;
  grid-template-columns: 10px 56px 1fr;
  align-items: center;
  gap: 7px;
  font-size: 0.74rem;
  color: #344767;
  margin-bottom: 5px;
}

.hp-pct {
  font-weight: 800;
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .alert-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .segment-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .two-col,
  .portfolio-grid {
    grid-template-columns: 1fr;
  }

  .growth-readout {
    grid-template-columns: 36px minmax(0, 1fr);
  }

  .growth-chips {
    grid-column: 2;
    justify-content: flex-start;
  }

}

@media (max-width: 768px) {
  .insight-page {
    padding: 12px;
  }

  .kpi-grid,
  .alert-grid,
  .segment-grid,
  .mini-grid,
  .composition-grid {
    grid-template-columns: 1fr;
  }

  .alert-board-head {
    flex-direction: column;
  }

  .revenue-risk-main,
  .revenue-risk-stats {
    grid-template-columns: 1fr;
  }

  .risk-wrap {
    flex-direction: column;
    align-items: stretch;
  }

  .chart-box.donut {
    width: 100%;
    flex-basis: auto;
  }

  .bar-row {
    grid-template-columns: 110px minmax(0, 1fr) 40px;
  }

  .card-head {
    flex-direction: column;
  }
}
</style>
